import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import { Card, CardBody, CardHeader, CardTitle, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { NetworkConceptsFilter, loadAllAccounts, selectFilteredAccountTree } from '../../redux/AccountsSlice';
import { getAllAddresses, selectLocations } from '../../redux/AddressSlice';
import { getLabelValue, getPublicName } from '../../redux/ConceptSlice';
import { Node, getReportingUnitsHierarchy, selectUnitHierarchy } from '../../redux/EntitiesSlice';
import { isAdmin } from '../../redux/MiscSlice';
import { Network, loadDefinitionNetworks, loadTableNetworks, selectDefinitionNetworks, selectTableNetworks } from '../../redux/NetworkSlice';
import { getReportYears, selectAvailableReportYears } from '../../redux/ReportSlice';
import { ActiveTaxonomyVersionInfo, TaxonomyTypeEnum, VersionRef, getReferenceForVersion, selectCurrentVersion, setTaxonomyVersion } from '../../redux/TaxonomyVersionSlice';
import { AdHocReportTile } from '../core/AdHocReportTile';
import { StyledSelectableListItem, getVersionedAccountsList } from '../core/ConceptManagementCore';
import { ListItem } from '../../redux/VisualizationSlice';
import { IReportFormat, defaultReportFormatValue, getSuffix, reportFormats } from '../feature/SystemReports';
import { addNode } from './AxisBuilder';
import { getUnitsListNoChanges } from './DistrictTypeManager';
import { AdHocQueryInput,AdHocReportStarter, generateAdHocReport, selectAdHocData, selectLoadingState,
    setAccounts, setDimensions, setEntities } from '../../redux/AdHocSlice';
import utilityHelper from '../../utils/utilityHelper';


interface AdhocReporterProps {
    preSelectedEntityList?: string[];
}

const AdHocReporter = (props: AdhocReporterProps) => {

    const dispatch = useAppDispatch();



    //selectors
    const isAdminUser: boolean = useAppSelector(isAdmin);
    const locationSelector = useAppSelector(selectLocations);
    const accountTree = useAppSelector(selectFilteredAccountTree);
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    var unitHierarchy: Node | null = useAppSelector(selectUnitHierarchy);
    var definitionNetworks: Network[] = useAppSelector(selectDefinitionNetworks);
    var tableNetworks: Network[] = useAppSelector(selectTableNetworks);
    var queryInput: AdHocQueryInput | null = useAppSelector(selectAdHocData);
    const availableReportYears = useAppSelector(selectAvailableReportYears);

    const loadingStateSelector = useAppSelector(selectLoadingState);

    //hooks
    const [versionYear, setVersionYear] = React.useState(0);
    const [locationChooserList, setLocationChooserList] = React.useState([]);

    //radio button hooks, single value

    //check box hooks, multiple values
    const [selectedYears, setSelectedYears] = React.useState<Array<ListItem>>([]);
    const [yearChoices, setYearChoices] = React.useState<string>("");
    const [entityChoices, setEntityChoices] = React.useState<string>("");
    const [accountChoices, setAccountChoices] = React.useState<string>("");
    const [hasSufficientQuery, setHasSufficientQuery] = React.useState<boolean>(false);
    const [selectedReportFormat, setSelectedReportFormat] = React.useState<IReportFormat>(defaultReportFormatValue);
    const [reportIsLoading, setReportIsLoading] = React.useState<boolean>(false);
    const [entityListItems, setEntityListItems] = React.useState<any[]>([]);

    const getConceptChild = function (n: Node): Node {
        if (n.concept) {
            return n;
        }
        if (!n.outgoing || n.outgoing.length === 0) {
            return n;
        }
        return getConceptChild(n.outgoing[0].to);

    }
    const getNodeOrConceptName = function (currentNode: Node): string {
        if (currentNode.name) {
            return currentNode.name;
        }
        if (currentNode.concept) {
            return currentNode.concept.name;
        }
        return "unknown";
    }
    const collectAxes = function (net: Network, currentNode: Node, axisUseMap: any, axList: Node[]) {
        var nodeName = getNodeOrConceptName(currentNode);
        if (nodeName.toLowerCase().indexOf("page") > 0) {
            return;
        }
        if (nodeName.indexOf("Axis") >= 0) {
            var axname = nodeName;
            var usedBy: Network[] = [];
            if (axisUseMap.hasOwnProperty(axname)) {
                usedBy = axisUseMap[axname];
            } else {
                axisUseMap[axname] = usedBy;
                axList.push(currentNode);
            }
            usedBy.push(net);
            return;
        }
        if (!currentNode.outgoing) {
            return;
        }
        currentNode.outgoing.map(e => collectAxes(net, e.to, axisUseMap, axList));
    }
    // List handling
    const getPublicNetworkName = function (n: Network): string {
        var result: string = n.name;
        if (result.indexOf("revenue_dwellings_and_fees") > 0) {
            return "Revenues - Impact Fees";
        }
        var idx: number = result.lastIndexOf("_");
        if (idx > 0) {
            result = utilityHelper.toTitleCase(result.substring(idx + 1).replace(/-/g, " "));
        }
        return result;
    }
    const getAxisLabel = function (root: Node, axisUseMap: any): string {
        var result: string = "";
        var usage: string[] = [];
        var result = getPublicName(root.concept);
        if (getLabelValue(root.concept, "terseLabel")) {
            return result;
        }

        var nodeName = getNodeOrConceptName(root);
        if (!axisUseMap.hasOwnProperty(nodeName)) {
            return "";
        }
        var usedBy: Network[] = axisUseMap[nodeName];
        usedBy.map(nUsed => {
            var netName = getPublicNetworkName(nUsed);
            if (usage.indexOf(netName) < 0) {
                usage.push(netName);
            }
        });
        if (usage.length > 0) {
            result += " (" + usage.join(",") + ")";
        }
        return result;
    }
    const getNetworkInfo = function (nlist: Network[]): StyledSelectableListItem[] {
        var alist: Array<StyledSelectableListItem> = [];
        if (nlist == null) {
            return [];
        }
        var axisUseMap: any = {};
        var axList: Node[] = [];
        for (var net of nlist) {
            if (net.name.indexOf("063") > 0) {
                continue;
            }
            if (net.roots) {
                net.roots.map(r => collectAxes(net, r, axisUseMap, axList))
            }
        }
        for (var root of axList) {
            var firstnet: Network | null = null;
            var usedBy: Network[] = axisUseMap[getNodeOrConceptName(root)];
            firstnet = usedBy[0];
            if (firstnet.name.indexOf("063") > 0) {
                firstnet = usedBy[1];
            }

            var listItem: StyledSelectableListItem = {
                networkid: firstnet.name,
                className: "",
                label: getAxisLabel(root, axisUseMap),
                value: firstnet.name + "#" + root.concept.id,
                disabled: false,
                checked: false,
                parentItem: null,
                children: []
            }
            alist.push(listItem);
            if (root.outgoing) {

                root.outgoing.map(e => addNode(listItem, getConceptChild(e.to)));
            }
        }
        alist.sort((a, b) => a.label.localeCompare(b.label));
        return alist;
    }
    const [axisList, setAxisList] = React.useState<StyledSelectableListItem[]>(getNetworkInfo(tableNetworks));


    const ddhandleAccountChange = function (currentNode: any, selectedNodes: any[]) {
        var accountsSelected: string[] = [];
        selectedNodes.map(sn => accountsSelected.push(sn.networkid + "#" + sn.value));
        setAccountChoices(accountsSelected.join(","));
        dispatch(setAccounts(accountsSelected));
    }

    const updateCheckedState = (items: any[], selectedValues: string[]): any[] => {
        return items.map(item => ({
            ...item,
            checked: selectedValues.includes(item.value),
            children: item.children ? updateCheckedState(item.children, selectedValues) : []
        }));
    }

    const findNodeByValue = (items: any[], entityCodeValue: string): any | undefined => {
        for (const item of items) {
            if (item.value && item.value.includes(entityCodeValue)) {
                return item;
            }
            if (item.children && item.children.length > 0) {
                const found = findNodeByValue(item.children, entityCodeValue);
                if (found) return found;
            }
        }
        return undefined;
    }

    const ddhandleLocationChange = function (currentNode: any, selectedNodes: any[]) {
        // 1. Get the values of the selected nodes
        const selectedValues = selectedNodes.map(sn => sn.value);

        // 2. Update checked state in entityListItems
        const updatedEntityListItems = updateCheckedState(entityListItems, selectedValues);

        // 3. Update state so DropdownTreeSelect reflects the selection
        setEntityListItems(updatedEntityListItems);

        // 4. Update Redux state as before
        setEntityChoices(selectedValues.join(","));
        dispatch(setEntities(selectedValues));
    }

    const ddhandleDimensionChange = function (currentNode: any, selectedNodes: any[]) {
        var dimensionsSelected: string[] = [];
        selectedNodes.map(sn => dimensionsSelected.push(sn.value));
        dispatch(setDimensions(dimensionsSelected));
    }

    const convertToYear = function (value: string | number): number {
        return parseInt(value.toString());
    }
    //functions
    const getYearSelections = function (): number[] {
        var yearsSelected: number[] = [];
        selectedYears.map(y => y.isChecked ? yearsSelected.push(convertToYear(y.value)) : "");
        return yearsSelected;
    }

    const handleYearChange = (listItem: ListItem) => {
        var foundIndex = selectedYears.findIndex((element) => element.value == listItem.value);
        if (foundIndex > -1) {
            //toggle list item
            selectedYears[foundIndex].isChecked = !selectedYears[foundIndex].isChecked;
        }
        setYearChoices(selectedYears.map(li => li.isChecked ? li.value : "x").join(","));
    }
    const createReportInput = function (): AdHocReportStarter {
        if (queryInput !== null) {
            var q: AdHocQueryInput = { ...queryInput, years: getYearSelections() };
            const inputData: AdHocReportStarter = {
                query: q,
                reportType: "ADHOC",
                fileType: selectedReportFormat.value
            };
            return inputData;
        } else {
            const emptyData: AdHocReportStarter = {
                query: null,
                reportType: "ADHOC",
                fileType: "UNKNOWN"
            };
            return emptyData;
        }
    }

    async function startReport(reportType: string, fileName: string) {
        if (queryInput !== null) {
            const inputData: AdHocReportStarter = createReportInput();
            const response = await dispatch(generateAdHocReport(inputData));
            const doc = unwrapResult(response);
            if (doc != null) {

                //TODO make this a generic function in document slice
                //that takes mime type, base64 encoded string, filename
                //and returns a blob.
                //TODO expand to handle xhtml and excel files
                var url = "data:" + doc.mimeType + ";base64," + doc.content;
                await fetch(url)
                    .then(res => res.blob())
                    .then(otho => {

                        const data = window.URL.createObjectURL(otho);
                        var link = document.createElement("a");
                        link.href = data;
                        link.download = fileName; //"report.xhtml";
                        link.click();
                        setTimeout(function () {
                            // For Firefox it is necessary to delay revoking the ObjectURL
                            window.URL.revokeObjectURL(data);
                        }, 100);
                    });
            }

            return doc;
        }
    }


    async function startGenerateAdHocReport() {
        setReportIsLoading(true);
        try {
            await startReport("ADHOC", "AdHoc Report" + getSuffix(selectedReportFormat.value));
            //            await startReport("AFR","Annual Financial Report.zip");
        } catch (err) {
            console.log(err);
            setReportIsLoading(false);
        }
        setReportIsLoading(false);
    }

    async function startGenerateAdHocReportPDF() {
        try {
            await startReport("ADHOCPDF", "AdHoc Report" + getSuffix(selectedReportFormat.value));
            //            await startReport("AFR","Annual Financial Report.zip");
        } catch (err) {
            console.log(err);
        }
    }
    async function startGenerateAdHocReportExcel() {
        try {
            await startReport("ADHOCExcel", "AdHoc Report" + getSuffix(selectedReportFormat.value));
            //            await startReport("AFR","Annual Financial Report.zip");
        } catch (err) {
            console.log(err);
        }
    }

    //side effects
    const initialSetup = function (years: number[]) {
        var lastYear = 2024;
        if (years && years.length > 0) {
            lastYear = years[years.length - 1];
        }
        var vInfo: ActiveTaxonomyVersionInfo | null = versionInfo;
        if (!vInfo) {
            vInfo = {
                year: lastYear,
                id: "temp",
                type: TaxonomyTypeEnum.AFR,
                periods: [],
                isPublished: true
            };
            dispatch(setTaxonomyVersion(vInfo));
        }
        var vYear: number = vInfo.year;
        var vRef: VersionRef = {
            year: vYear,
            type: TaxonomyTypeEnum.AFR
        };
        setVersionYear(vYear);
        dispatch(getReportingUnitsHierarchy(vRef));
        dispatch(loadDefinitionNetworks(vInfo));
        dispatch(loadTableNetworks(vInfo));
        if (tableNetworks.length > 0) {
            setAxisList(getNetworkInfo(tableNetworks));
        }

        if (locationSelector === null ||
            (locationSelector !== null && locationSelector.length === 0)) {
            dispatch(getAllAddresses());
        }

        var accountQuery: NetworkConceptsFilter = {
            nameFilter: "*Title+;money",
            taxonomyVersion: getReferenceForVersion(vInfo, 2024)
        }
        dispatch(loadAllAccounts(accountQuery));
    }

    React.useEffect(() => {
        // Re-enabled after factoring into separate container (was using previous tabbed container to load years for all)
        dispatch(getReportYears(TaxonomyTypeEnum.AFR)).then((result: any) => initialSetup(result.payload));
    }, [])

    React.useEffect(() => {
        //TODO make this an api call for available years
        setAxisList(getNetworkInfo(tableNetworks));
    }, [tableNetworks])

    React.useEffect(() => {
        var hasSufficient: boolean = true;
        var fmt: string = selectedReportFormat.value;
        if (fmt.length === 0) {
            hasSufficient = false;
        }
        if (fmt === 'UNKNOWN') {
            hasSufficient = false;
        }
        if (getYearSelections().length === 0) {
            hasSufficient = false;
        }
        if (accountChoices.length === 0) {
            hasSufficient = false;
        }
        if (entityChoices.length === 0) {
            hasSufficient = false;
        }
        setHasSufficientQuery(hasSufficient);

    }, [selectedReportFormat, yearChoices, accountChoices, entityChoices])

    React.useEffect(() => {
        setSelectedYears(availableReportYears.map((x: number) => { return { label: x, value: x, isChecked: false } }).reverse());
    }, [availableReportYears])

    const subSelector = React.useMemo(() => {
        return (
            <DropdownTreeSelect
                data={locationChooserList}
                keepTreeOnSearch={true}
                onChange={ddhandleLocationChange}
                texts={{ placeholder: "Add Location..." }} />
        )
    }, [accountTree]);

    const accountSelector = React.useMemo(() => {
        var isPublic: boolean = !isAdminUser;

        return (
            <DropdownTreeSelect
                data={getVersionedAccountsList(versionInfo, null, accountTree, [], [], false, false, isPublic)}
                keepTreeOnSearch={true}
                onChange={ddhandleAccountChange}
                texts={{ placeholder: "Add Account..." }} />
        )
    }, [accountTree]);

    const entitySelector =
        (
            <DropdownTreeSelect
                data={entityListItems}
                keepTreeOnSearch={true}
                onChange={ddhandleLocationChange}
                texts={{ placeholder: "Add Entity..." }} />

        );

    const dimensionSelector = React.useMemo(() => {
        return (
            <DropdownTreeSelect
                id="dimensionPicker"
                data={axisList}
                keepTreeOnSearch={true} keepChildrenOnSearch={true}
                onChange={ddhandleDimensionChange}
                texts={{ placeholder: "Add Dimension..." }} />
        )
    }, [tableNetworks, axisList]);

    function isReportFormatSelected(reportFormatValue: string): boolean {
        var result = false;

        if (reportFormatValue) {
            if (selectedReportFormat.value == reportFormatValue) {
                result = true;
            }
        }
        return result;
    }
    function reportFormatUnavailable(value: string) {
        var unavailable: boolean = false;
        switch (value.toLowerCase()) {
            case 'exxcel':
            case 'pxdf':
                unavailable = true;
                break;
        }
        return unavailable;
    }

    const reportFormatRender = (<div>
        <ul>
            {reportFormats.map(({ value, label }) => (
                <li key={value}>
                    <Input
                        type="radio"
                        value={value}
                        name="adhoc-report-formats"
                        disabled={reportFormatUnavailable(value)}
                        onChange={(e) => {
                            setSelectedReportFormat({ value: value, label: label });
                        }}
                        checked={isReportFormatSelected(value)} />
                    {label}
                </li>
            ))}
        </ul>
    </div>);

    React.useEffect(() => {

        if (props.preSelectedEntityList && props.preSelectedEntityList.length > 0 && unitHierarchy) {
            var entityCodeValue = props.preSelectedEntityList[0];
            var foundItem = findNodeByValue(entityListItems, entityCodeValue);
            if (foundItem) {
                foundItem!.checked = true;
                ddhandleLocationChange(foundItem, [foundItem]);
            }
        }
    }, [props.preSelectedEntityList])

    React.useEffect(() => {

        if(unitHierarchy) {
            var unitSelectItems = getUnitsListNoChanges(unitHierarchy);
            setEntityListItems(unitSelectItems);
        }

    }, [unitHierarchy])


    return (
        <React.Fragment>
            <Row>
                <Col>
                    <Card>
                        <CardHeader
                            style={{
                                color: "white",
                                backgroundColor: "#458BCA",
                                borderRadius: "0px",
                                fontWeight: "initial"
                            }}
                        >Choose Criteria</CardHeader>
                        <CardBody>
                            <Card>
                                <CardBody>
                                    <CardTitle>Select one or more entities</CardTitle>
                                    {entitySelector}
                                </CardBody>
                            </Card>
                            <Card>
                                <CardBody>
                                    <CardTitle>Select one or more years</CardTitle>
                                    {selectedYears.map((afrYearValue) => (
                                        <FormGroup key={afrYearValue.value}
                                            check
                                            inline
                                        >
                                            <Input type='checkbox'
                                                defaultChecked={afrYearValue.isChecked}

                                                value={afrYearValue.value}
                                                disabled={loadingStateSelector == 'loading'}
                                                onChange={(e) => {
                                                    handleYearChange(afrYearValue);
                                                }}


                                            />
                                            <Label check>
                                                {afrYearValue.label}
                                            </Label>
                                        </FormGroup>
                                    ))}
                                </CardBody>
                            </Card>
                            <Card>
                                <CardBody>
                                    <CardTitle>Select one or more accounts</CardTitle>
                                    {accountSelector}
                                </CardBody>
                            </Card>
                            <Card>
                                <CardBody>
                                    <CardTitle>Select dimensions (OPTIONAL)</CardTitle>
                                    {dimensionSelector}
                                </CardBody>
                            </Card>

                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    <Card>
                        <CardHeader
                            style={{
                                color: "white",
                                backgroundColor: "#458BCA",
                                borderRadius: "0px",
                                fontWeight: "initial"
                            }}
                        >Choose Report Format</CardHeader>
                        <CardBody>
                            {reportFormatRender}
                        </CardBody>
                    </Card>
                    <Card>
                        <CardHeader
                            style={{
                                color: "white",
                                backgroundColor: "#458BCA",
                                borderRadius: "0px",
                                fontWeight: "initial"
                            }}
                        >Get Report</CardHeader>
                        <CardBody>
                            <AdHocReportTile
                                icon="bi bi-download"
                                actionType="AdHoc"
                                isEnabled={hasSufficientQuery}
                                clickAction={startGenerateAdHocReport}
                                tileTitle="AdHoc Report (iXBRL)"
                                tileDescription="Get Custom Report"
                                isLoading={reportIsLoading}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
}

export default AdHocReporter;